/*
 * Copyright (c) 2025-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

// collapsible-group-header.component.ts
import {Component} from '@angular/core';
import {Column, IHeaderGroupParams} from 'ag-grid-community';

@Component({
    selector: 'app-collapsible-group-header',
    template: `
        <div class="group-header-container">
            <span class="group-header-title">{{ params.displayName }}</span>
            <div class="collapse-toggle" (click)="toggleGroup($event)">
                <i class="pi" [ngClass]="expanded ? 'pi-angle-double-left' : 'pi-angle-double-right'"
                title="Collapse/expand column children."></i>
            </div>
        </div>
    `,
    styles: [`
        :host {
            display: block;
            width: 100%;
            height: 100%;
        }

        .group-header-container {
            position: relative;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center; /* center the title */
            padding: 0 20px; /* space for the button on the right */
            box-sizing: border-box;
        }

        .group-header-title {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            text-align: center;
        }

        .collapse-toggle {
            position: absolute;
            top: 2px;
            right: 2px;
            background: none;
            border: none;
            font-weight: bold;
            cursor: pointer;
            font-size: 12px;
              padding: 0;
        }
        .placeholder-cell {
          color: #666;
          font-style: normal;
          text-align: center;
          opacity: 0.85;
        }

    `],
    standalone: false
})
export class CollapsibleGroupHeaderComponent {
    public params!: IHeaderGroupParams;
    public expanded = true;

    // cache of actual columns for each groupId
    private cachedGroupColumns = new Map<string, Column[]>();
    // cache of placeholder colId for each groupId
    private cachedPlaceholder = new Map<string, string>();

    agInit(params: IHeaderGroupParams): void {
        this.params = params;

        const group = params.columnGroup;
        if (!group) {
            return;
        }

        // Determine a stable group identifier to use as our cache key.
        // Use column group id if available, otherwise fallback to the displayName.
        const groupId = (typeof group.getGroupId === 'function' && group.getGroupId()) ||
                         params.displayName || `grp:${Math.random()}`;

        // Try to discover placeholder and leaf columns now.
        // 1) Collect leaf columns via group if possible.
        let leafCols: Column[];
        try {
            // If AG Grid exposes getLeafColumns now, use it
            if ((group as any).getLeafColumns) {
                leafCols = (group as any).getLeafColumns() as Column[];
            } else {
                // fallback: walk children
                leafCols = this.collectLeafColumnsFromChildren(group);
            }
        } catch (e) {
            leafCols = this.collectLeafColumnsFromChildren(group);
        }

        // Remove any placeholder-like column from leafCols when caching real children
        const possiblePlaceholder = this.findPlaceholderColId(groupId, leafCols);
        const realCols = leafCols.filter(c => !this.isPlaceholderColId(c.getColId(), groupId));

        // Cache them for later. If no realCols discovered, we will attempt to derive from column model on demand.
        if (realCols.length > 0) {
            this.cachedGroupColumns.set(groupId, realCols);
        }

        if (possiblePlaceholder) {
            this.cachedPlaceholder.set(groupId, possiblePlaceholder);
        } else {
            // try to find placeholder in global column model by pattern
            const placeHolder = this.findPlaceholderGlobally(groupId);
            if (placeHolder) {
                this.cachedPlaceholder.set(groupId, placeHolder);
            }
        }

        // Keep the groupId as a map key on params for later use
        (this.params as any).__collapsibleGroupId = groupId;
    }

    // Collect leaf columns by walking children (safe fallback)
    private collectLeafColumnsFromChildren(group: any): Column[] {
        const result: Column[] = [];
        const walk = (node: any) => {
            const children = node.getChildren?.() ?? [];
            for (const child of children) {
                if ((child as any).getColId) {
                    result.push(child as Column);
                } else if ((child as any).getChildren) {
                    walk(child);
                }
            }
        };
        walk(group);
        return result;
    }

    private isPlaceholderColId(colId: string, groupId: string): boolean {
        return colId?.endsWith(`${groupId}__placeholder`);
    }

    private findPlaceholderColId(groupId: string, leafCols: Column[]): string | null {
        for (const c of leafCols) {
            const id = c.getColId();
            if (this.isPlaceholderColId(id, groupId)) {
                return id;
            }
        }
        return null;
    }

    // fallback: scan all columns in the grid for a placeholder matching groupId pattern
    private findPlaceholderGlobally(groupId: string): string | null {
        try {
            const all = (this.params.api as any).getAllGridColumns?.() ?? (this.params.api as any).getColumnModel?.()?.getAllGridColumns?.();
            if (!all) {
                return null;
            }
            for (const c of all) {
                const id = c.getColId?.();
                if (this.isPlaceholderColId(id, groupId)) {
                    return id;
                }
            }
        } catch (e) {
            /* ignore */
        }
        return null;
    }

    toggleGroup(event: MouseEvent): void {
        event.stopPropagation();

        const group = this.params.columnGroup;
        if (!group) {
            return;
        }

        // groupId stored in agInit
        const groupId = (this.params as any).__collapsibleGroupId ||
                        ((group.getGroupId && group.getGroupId()) || this.params.displayName);

        // ensure we have a cached set of real columns (try to populate from column model if missing)
        if (!this.cachedGroupColumns.has(groupId)) {
            const runtimeCols = this.collectLeafColumnsFromChildren(group);
            const realCols = runtimeCols.filter(c => !this.isPlaceholderColId(c.getColId(), groupId));
            if (realCols.length > 0) {
                this.cachedGroupColumns.set(groupId, realCols);
            }
        }

        // make sure we have a placeholder col id
        if (!this.cachedPlaceholder.has(groupId)) {
            let placeHolder = this.findPlaceholderColId(groupId, this.cachedGroupColumns.get(groupId) ?? []) ||
                              this.findPlaceholderGlobally(groupId);
            if (placeHolder) {
                this.cachedPlaceholder.set(groupId, placeHolder);
            }
        }

        const placeholderColId = this.cachedPlaceholder.get(groupId) ?? null;
        const realCols = this.cachedGroupColumns.get(groupId) ?? [];

        this.expanded = !this.expanded;

        // Build state. If no placeholder was found, we must ensure at least one column remains visible.
        const states: Array<{ colId: string; hide: boolean }> = [];

        if (placeholderColId) {
            // When expanded -> hide placeholder; when collapsed -> show placeholder
            states.push({ colId: placeholderColId, hide: this.expanded });
        }

        if (realCols.length > 0) {
            for (const col of realCols) {
                states.push({ colId: col.getColId(), hide: !this.expanded });
            }
        }

        // Safety fallback: if we found no states (no placeholder and no cached children),
        // attempt a safe toggle that leaves at least one column visible in the group.
        if (states.length === 0) {
            const allLeaf = (group as any).getLeafColumns?.() ?? [];
            if (allLeaf.length > 1) {
                // hide all but first when collapsing, or show all when expanding
                if (!this.expanded) {
                    // collapsing -> hide except first
                    for (let i = 1; i < allLeaf.length; i++) {
                        states.push({ colId: allLeaf[i].getColId(), hide: true });
                    }
                } else {
                    // expanding -> show all
                    for (let i = 0; i < allLeaf.length; i++) {
                        states.push({ colId: allLeaf[i].getColId(), hide: false });
                    }
                }
            }
        }

        if (states.length > 0) {
            console.debug('Applying column state:', states);
            this.params.api.applyColumnState({ state: states, applyOrder: false });
        } else {
            // last resort: do nothing; avoid hiding everything and leaving no UI
            console.warn('CollapsibleGroupHeaderComponent: no columns found to toggle for group', groupId);
        }
    }
}
